// ── SectionToolbar — per-section price-mode + market-side chips ────
//
// Compact chip-group toolbar shown in the header of each GameDetail
// market section (Moneyline, Spread, Total). Independent state per
// section so the page doesn't reach across surfaces — analogous to the
// value-dashboard's global toolbar but scoped to one card.

import { cn } from '@/lib/infra/utils';
import type { MarketSectionViewMode } from '@/stores/displaySettings';
import type { MarketSide, PriceMode } from '../ValueDashboard/types';
import { OddsFilterChips } from '../ValueDashboard/OddsFilterChips';

export function SectionToolbar({
  priceMode,
  onPriceMode,
  marketSide,
  onMarketSide,
  viewMode,
  onViewMode,
  showPolyOnlyLines,
  onShowPolyOnlyLines,
}: {
  priceMode: PriceMode;
  onPriceMode: (m: PriceMode) => void;
  marketSide: MarketSide;
  onMarketSide: (s: MarketSide) => void;
  /** Compact/Detailed density toggle. Rendered only when both are supplied —
   *  the Moneyline section (single view) omits them. */
  viewMode?: MarketSectionViewMode;
  onViewMode?: (m: MarketSectionViewMode) => void;
  /** D-020 LT2-005 — reveal toggle for Poly-only (absent-Kalshi) lines.
   *  Rendered only when both are supplied (lt2-gated by the caller). */
  showPolyOnlyLines?: boolean;
  onShowPolyOnlyLines?: (show: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      {/* Odds filter (Dan 2026-08-23): the same U|OP chips as the
          dashboard table headers, so the demo toggle rides EVERY GameDetail
          section too. Global persisted settings — the sections already obey
          them (DNL-001); this just puts the control where the table is. */}
      <OddsFilterChips />
      {onShowPolyOnlyLines != null && showPolyOnlyLines != null && (
        <ChipGroup>
          <Chip
            active={showPolyOnlyLines}
            onClick={() => onShowPolyOnlyLines(!showPolyOnlyLines)}
          >
            Poly-only
          </Chip>
        </ChipGroup>
      )}
      {viewMode && onViewMode && (
        <ChipGroup>
          {(['compact', 'detailed'] as MarketSectionViewMode[]).map((m) => (
            <Chip key={m} active={viewMode === m} onClick={() => onViewMode(m)}>
              {m === 'compact' ? 'Compact' : 'Detailed'}
            </Chip>
          ))}
        </ChipGroup>
      )}
      <ChipGroup>
        {(['maker', 'taker', 'market'] as PriceMode[]).map((m) => (
          <Chip key={m} active={priceMode === m} onClick={() => onPriceMode(m)}>
            {m === 'maker' ? 'Maker' : m === 'taker' ? 'Taker' : 'Market'}
          </Chip>
        ))}
      </ChipGroup>
      <ChipGroup>
        {(['yes', 'no'] as MarketSide[]).map((s) => (
          <Chip
            key={s}
            active={marketSide === s}
            onClick={() => onMarketSide(s)}
            tone={s === 'yes' ? 'positive' : 'negative'}
          >
            {s.toUpperCase()}
          </Chip>
        ))}
      </ChipGroup>
    </div>
  );
}

export function ChipGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-border/60 inline-flex items-center rounded border bg-muted/40 p-0.5">
      {children}
    </div>
  );
}

export function Chip({
  active,
  onClick,
  tone,
  children,
}: {
  active: boolean;
  onClick: () => void;
  tone?: 'positive' | 'negative';
  children: React.ReactNode;
}) {
  const activeClass =
    tone === 'positive'
      ? 'bg-positive/20 text-positive ring-1 ring-positive/40'
      : tone === 'negative'
        ? 'bg-negative/20 text-negative ring-1 ring-negative/40'
        : 'bg-primary text-primary-foreground';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors',
        active ? activeClass : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
